import React, { useState, memo, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Text, TextInput, NumberInput } from '@simplybenefits/sb-ui-library';

/**
 * Organize Class titles alphabetically and numerically 
 * @param {*} a 
 * @param {*} b 
 * @returns 
 */
const compareTitles = (a, b) => {
    const normalizedA = a.title.toLowerCase().replace(/ /g, '');
    const normalizedB = b.title.toLowerCase().replace(/ /g, '');

    // If titles are the same without numbers, sort by number
    if (normalizedA.replace(/[0-9]/g, '') === normalizedB.replace(/[0-9]/g, '')) {
        return Number(normalizedA.replace(/^\D+/g, '')) - Number(normalizedB.replace(/^\D+/g, '')); 
    }

    // Otherwise, sort alphabetically
    return a.title.localeCompare(b.title);
};

/**
 * RenewalDropDown component renders an accordion with input fields for different benefit classes based on benefitType.
 * @param {string} param0.benefitType - The type of benefit (e.g., 'life', 'health', etc.).
 * @param {Object} param0.benefitClasses - An object containing the benefit classes and their values.
 * @param {Object} param0.classMeta - Metadata for each class, containing titles and other details.
 * @param {function} param0.handleInputChange - Function to handle input changes for the benefit form.
 * @param {Object} param0.errors - An object containing error messages for form validation.
 */
const RenewalBenefitAccordion = memo(({ benefitType, benefitClasses, classMeta, handleInputChange, errors }) => {
    const [ isExpanded, setExpanded ] = useState(false);
    
    const benefitMap = {
        'life': 'Life',
        'add': 'AD&D',
        'ci': 'Critical Illness',
        'ciSpouse': 'Critical Illness Spouse',
        'ciChild': 'Critical Illness Child',
        'depLife': 'Dependent Life',
        'ltd': 'Long Term Disability',
        'std': 'Short Term Disability',
        'health': 'Health',
        'outOfCountry': 'Out of Country',
        'dental': 'Dental',
        'efap': 'EFAP',
        'financialAwareness': 'Financial Awareness',
        'virtualHealthcare': 'Virtual Health'
    };

    const styles = {
        claimInputs: { 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 20 
        },
        classPricing: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 20 
        }
    };

    const hasClaimInputs = [ 'Life', 'Dependent Life', 'Critical Illness', 'Critical Illness Spouse', 'Critical Illness Child', 'AD&D', 'Out of Country', 'Long Term Disability', 'Short Term Disability' ].includes(benefitMap[benefitType]);

    useEffect(() => {
        // Check if there are any errors, ensure the Accordion is open if so 
        const hasErrors = Object.values(errors?.premiumIllustration?.[benefitType] || {}).some((classItem) => Object.values(classItem).some((value) => value !== null));
        
        if (hasErrors) {
            setExpanded(true);
        } 
    }, [ errors ]);

    if (Object.keys(benefitClasses).length === 0) {
        return null;
    }

    return (
        <>  
            <Accordion 
                key={ benefitType } 
                style={{ boxShadow: 'none', padding: 0, margin: 0 }} 
                expanded={ isExpanded } 
                onChange={(_, expandedState) => setExpanded(expandedState)}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ padding: 0, minHeight: 60 }}>
                    <Text bold customStyle={{ fontSize: 19 }}>
                        { benefitMap[benefitType] }
                    </Text>
                </AccordionSummary>
                {
                    isExpanded ?
                        <AccordionDetails style={{ padding: 0 }}>
                            {
                                Object.entries(benefitClasses)
                                    .sort(([ classID1 ], [ classID2 ]) => compareTitles(classMeta[classID1], classMeta[classID2]))
                                    .map(([ classID, classItem ]) => (
                                        <div key={classID} style={{ marginBottom: 15 }}>
                                            <Text bold medium color="primary" style={{ marginBottom: 20 }}>
                                                { classMeta[classID].title }
                                            </Text>
                                            <div style={ styles.classPricing }>
                                                <NumberInput
                                                    label={ 'Carrier Change Percentage' }
                                                    value={ classItem.carrierChangePercentage }
                                                    onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'carrierChangePercentage' ], event)}
                                                    error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.carrierChangePercentage) }
                                                    helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.carrierChangePercentage }
                                                    suffix
                                                    allowNegative={true}
                                                    limitOneHundred={'false'}
                                                    fullWidth
                                                />
                                                <NumberInput
                                                    label={ 'Discount' }
                                                    value={ classItem.discountPercentage }
                                                    onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'discountPercentage' ], event)}
                                                    error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.discountPercentage) }
                                                    helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.discountPercentage }
                                                    suffix
                                                    fullWidth
                                                />
                                                <NumberInput
                                                    label={ 'Commission Percentage' }
                                                    value={ classItem.commissionPercentage }
                                                    onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'commissionPercentage' ], event)}
                                                    error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.commissionPercentage) }
                                                    helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.commissionPercentage }
                                                    suffix
                                                    decimalPlaces="3"
                                                    fullWidth
                                                />
                                                <NumberInput
                                                    label={ 'Markup Percentage' }
                                                    value={ classItem.markupPercentage }
                                                    onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'markupPercentage' ], event)}
                                                    error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.markupPercentage) }
                                                    helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.markupPercentage }
                                                    suffix
                                                    decimalPlaces="3"
                                                    fullWidth
                                                />
                                                <TextInput
                                                    label={ 'Carrier' }
                                                    value={ classItem.carrier }
                                                    onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'carrier' ], event)}
                                                    error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.carrier) }
                                                    helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.carrier }
                                                    selectOptions={[ 'Sunlife', 'Simply Benefits', 'La Capitale', 'Equitable Life', 'Empire', 'Orion', 'SSQ', 'Kii', 'Advica', 'AIG', 'Allstate', 'ASO', 'Maple', 'Gotodoctor', 'Dialogue', 'Camden', 'Fenchurch', 'Wawanesa', 'Desjardins', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Enriched Academy', 'Assumption Life', 'CHUBB', 'Humania Assurance' ].sort()}
                                                    fullWidth
                                                    select
                                                />
                                            </div>

                                            {
                                                hasClaimInputs ? 
                                                    <div style={ styles.claimInputs }>
                                                        <NumberInput
                                                            label={ 'Number of Paid Claims' }
                                                            value={ classItem.numberOfPaidClaims }
                                                            onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'numberOfPaidClaims' ], event)}
                                                            error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.numberOfPaidClaims) }
                                                            helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.numberOfPaidClaims }
                                                            fullWidth
                                                        />
                                                        <NumberInput
                                                            label={ 'Paid Amount of Claims' }
                                                            value={ classItem.totalAmountOfClaimsPaid }
                                                            onChange={ (event) => handleInputChange([ 'premiumIllustration', `${ benefitType }`, `${ classID }`, 'totalAmountOfClaimsPaid' ], event)}
                                                            error={ Boolean(errors?.premiumIllustration?.[benefitType]?.[classID]?.totalAmountOfClaimsPaid) }
                                                            helperText={ errors?.premiumIllustration?.[benefitType]?.[classID]?.totalAmountOfClaimsPaid }
                                                            prefix
                                                            fullWidth
                                                        />  
                                                    </div>
                                                    : null
                                            }
                                        </div>
                                    ))
                            }
                        </AccordionDetails>
                        : null
                }   
            </Accordion>
        </>
    );
});

export default RenewalBenefitAccordion;